import React, { useState } from 'react';
import { sincronizarNotaPorChave } from '../services/api';

const SincronizacaoManualNota = ({ onSuccess, onError, onClose, hideButton = false, isFloating = false }) => {
    const [chaveAcesso, setChaveAcesso] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [localMessage, setLocalMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'error' ou 'info'

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalMessage(''); // Limpa mensagens anteriores
        
        // Remove espaços para validação e envio
        const chaveLimpa = chaveAcesso.replace(/ /g, '');
        
        if (!chaveLimpa.trim()) {
            setLocalMessage('Por favor, informe a chave de acesso da nota fiscal');
            setMessageType('error');
            return;
        }

        if (chaveLimpa.length !== 44) {
            setLocalMessage('A chave de acesso deve conter exatamente 44 caracteres');
            setMessageType('error');
            return;
        }

        setIsLoading(true);
        
        try {
            const result = await sincronizarNotaPorChave(chaveLimpa);
            
            setChaveAcesso('');
            setShowModal(false);
            setLocalMessage('');
            
            // Fechar modal se estiver sendo usado no botão flutuante
            if (onClose) {
                onClose();
            }
            
            // Criar mensagem mais detalhada para o modal de sucesso
            let successMessage = result.message || 'Sincronização iniciada com sucesso!';
            
            if (result.data) {
                successMessage += `\n\nChave: ${result.data.chave}`;
                if (result.data.observacao) {
                    successMessage += `\n\n${result.data.observacao}`;
                }
            }
            
            onSuccess && onSuccess(successMessage);
            
        } catch (error) {
            console.error('Erro na sincronização:', error);
            
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               'Erro ao sincronizar nota fiscal';
            
            setLocalMessage(errorMessage);
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChaveChange = (e) => {
        // Sem processamento automático - apenas atualiza o valor
        setChaveAcesso(e.target.value);
        
        // Limpar mensagem de validação quando o usuário começar a digitar
        if (localMessage) {
            setLocalMessage('');
            setMessageType('');
        }
    };

    const limparEspacos = () => {
        // Remove apenas espaços quando o usuário clicar no botão
        const chaveLimpa = chaveAcesso.replace(/ /g, '');
        setChaveAcesso(chaveLimpa);
    };

    const handleCancel = () => {
        setChaveAcesso('');
        setLocalMessage('');
        setMessageType('');
        setShowModal(false);
        
        // Fechar modal se estiver sendo usado no botão flutuante
        if (onClose) {
            onClose();
        }
    };

    return (
        <>
            {/* Botão para abrir o modal - só mostra se hideButton for false */}
            {!hideButton && (
                <button
                    onClick={() => setShowModal(true)}
                    className={isFloating 
                        ? "w-14 h-14 bg-stone-600 hover:bg-stone-700 text-white rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110"
                        : "bg-stone-600 hover:bg-stone-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors shadow-sm"
                    }
                    title="Sincronizar nota fiscal manualmente por chave de acesso"
                >
                    <svg className={isFloating ? "w-6 h-6" : "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {!isFloating && <span className="text-sm">Sincronizar Nota</span>}
                </button>
            )}

            {/* Modal - só mostra quando showModal for true E hideButton for false */}
            {showModal && !hideButton && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Sincronizar Nota Fiscal
                            </h3>
                            <button
                                onClick={handleCancel}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Chave de Acesso da Nota Fiscal
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={chaveAcesso}
                                        onChange={handleChaveChange}
                                        placeholder="Digite a chave de 44 caracteres"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                        disabled={isLoading}
                                    />
                                    {chaveAcesso.includes(' ') && (
                                        <button
                                            type="button"
                                            onClick={limparEspacos}
                                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md text-xs transition-colors"
                                            title="Remover espaços"
                                        >
                                            Limpar
                                        </button>
                                    )}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Caracteres: {chaveAcesso.length} 
                                    {chaveAcesso.includes(' ') && (
                                        <span className="text-blue-600">
                                            (válidos: {chaveAcesso.replace(/ /g, '').length}/44)
                                        </span>
                                    )}
                                    {!chaveAcesso.includes(' ') && chaveAcesso.length > 0 && (
                                        <span className="text-blue-600">/44</span>
                                    )}
                                    {chaveAcesso.includes(' ') && (
                                        <span className="text-orange-500 ml-2">
                                            • Contém espaços - clique em "Limpar"
                                        </span>
                                    )}
                                </div>

                                {/* Mensagem de validação local */}
                                {localMessage && (
                                    <div className={`mt-2 p-3 rounded-md text-sm ${
                                        messageType === 'error' 
                                            ? 'bg-red-50 border border-red-200 text-red-700' 
                                            : 'bg-blue-50 border border-blue-200 text-blue-700'
                                    }`}>
                                        <div className="flex items-center">
                                            {messageType === 'error' ? (
                                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            {localMessage}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                                <div className="flex">
                                    <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <div className="text-sm text-blue-700">
                                        <p className="font-medium mb-1">Como usar:</p>
                                        <ul className="text-xs space-y-1">
                                            <li>• Cole a chave de acesso de 44 caracteres</li>
                                            <li>• A sincronização será processada em alguns instantes</li>
                                            <li>• Verifique a listagem de notas fiscais após alguns minutos</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    disabled={isLoading}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading || chaveAcesso.replace(/ /g, '').length !== 44}
                                    className="px-4 py-2 bg-stone-600 text-white rounded-md hover:bg-stone-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sincronizando...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            Sincronizar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Formulário inline - mostra quando hideButton for true */}
            {hideButton && (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="chaveAcesso" className="block text-sm font-medium text-gray-700 mb-2">
                            Chave de Acesso (44 caracteres)
                        </label>
                        <div className="flex gap-2">
                            <input
                                id="chaveAcesso"
                                type="text"
                                value={chaveAcesso}
                                onChange={handleChaveChange}
                                placeholder="Digite a chave de 44 caracteres"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                disabled={isLoading}
                            />
                            {chaveAcesso.includes(' ') && (
                                <button
                                    type="button"
                                    onClick={limparEspacos}
                                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md text-xs transition-colors"
                                    title="Remover espaços"
                                >
                                    Limpar
                                </button>
                            )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                            Caracteres: {chaveAcesso.length} 
                            {chaveAcesso.includes(' ') && (
                                <span className="text-blue-600">
                                    (válidos: {chaveAcesso.replace(/ /g, '').length}/44)
                                </span>
                            )}
                            {!chaveAcesso.includes(' ') && chaveAcesso.length > 0 && (
                                <span className="text-blue-600">/44</span>
                            )}
                            {chaveAcesso.includes(' ') && (
                                <span className="text-orange-500 ml-2">
                                    • Contém espaços - clique em "Limpar"
                                </span>
                            )}
                        </div>

                        {/* Mensagem de validação local */}
                        {localMessage && (
                            <div className={`mt-2 p-3 rounded-md text-sm ${
                                messageType === 'error' 
                                    ? 'bg-red-50 border border-red-200 text-red-700' 
                                    : 'bg-blue-50 border border-blue-200 text-blue-700'
                            }`}>
                                <div className="flex items-center">
                                    {messageType === 'error' ? (
                                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    {localMessage}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <div className="flex">
                            <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="text-sm text-blue-700">
                                <p className="font-medium">Como encontrar a chave de acesso:</p>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                    <li>Em NFe em PDF: localizada no canto superior direito</li>
                                    <li>Em NFe em XML: dentro da tag &lt;infNFe Id="NFe..."&gt;</li>
                                    <li>É uma sequência de 44 números</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !chaveAcesso.trim()}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sincronizando...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Sincronizar
                                </>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </>
    );
};

export default SincronizacaoManualNota;